import React from 'react';
import {
    Avatar,
    Box,
    Button,
    Hidden,
    Paper,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { withSnackbar } from 'notistack';
import {
    GridContainer,
    GridItem,
    ScreenContainer,
    SocialMediaLink,
    UserBadge,
} from '../../components';
import RelatedPostsGrid from './RelatedPostsGrid';
import PropTypes from 'prop-types';
import styles from './styles';
import withLoading from '../../hocs/withLoading';
import { useRouter } from 'next/router';
import { NAVIGATION_ROUTES } from '../../config/NavigationRoutes';
import { AuthImageSrc } from '../../config/staticAssets';
import { NextSeo } from 'next-seo';

const User = (props) => {
    const {
        classes,
        profile,
        isOwner,
        loading,
        renderLoading,
        error,
        refetch,
        preferredUsername,
    } = props;

    const router = useRouter();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (loading) return renderLoading();
    else if (error)
        return (
            <Box
                height={'70vh'}
                width={'100%'}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Typography>
                    Error retrieving profile <b>{preferredUsername}</b>
                </Typography>
                <Button color={'primary'} onClick={refetch}>
                    Retry
                </Button>
            </Box>
        );

    const {
        role,
        displayName,
        avatar,
        banner,
        bio,
        socialMedia,
        preferred,
        verified,
        permissionGroup,
    } = profile;

    const isElevatorStaff =
        permissionGroup.groupName !== 'Standard' || role === 'Contributor';

    /* NOTE implement following logic */
    const onFollowClick = () => {
        console.log('NOT HANDLED');
    };

    const onEditClick = () => {
        if (isOwner) router.replace(NAVIGATION_ROUTES.PROFILE.EDIT_PROFILE.route);
    };

    const renderBannerImage = () => {
        let imgSrc = AuthImageSrc;

        if (banner)
            imgSrc = {
                src: banner.sourceUrl,
                srcSet: banner.sourceSet,
            };

        return <img {...imgSrc} className={classes.bannerImage} />;
    };

    const renderProfileImage = () => {
        return (
            <Box paddingRight={2}>
                <Avatar
                    src={avatar && avatar.sourceUrl}
                    srcSet={avatar && avatar.sourceSet}
                    className={classes.profileImage}
                >
                    {preferredUsername[0].toUpperCase()}
                </Avatar>
            </Box>
        );
    };

    const renderSocialMedia = () => {
        return (
            <GridContainer
                direction={'row'}
                style={{
                    //border: '1px solid red',
                    width: 'auto',
                }}
            >
                {socialMedia.map(({ type, link }, index) => (
                    <GridItem key={index}>
                        <Box paddingRight={mobile ? 3 : 2}>
                            <SocialMediaLink type={type} link={link} />
                        </Box>
                    </GridItem>
                ))}
            </GridContainer>
        );
    };

    const renderActionButton = (text, Icon, onClick) => {
        return (
            <Button
                fullWidth={mobile}
                variant={'outlined'}
                size={mobile ? 'large' : 'small'}
                className={classes.button}
                onClick={onClick}
            >
                {Icon}
                {text}
            </Button>
        );
    };

    const renderFollowButton = () => {
        return renderActionButton('Follow', null, onFollowClick);
    };

    const renderEditProfileButton = () => {
        return renderActionButton('Edit Profile', null, onEditClick);
    };

    const renderBio = () => {
        return (
            <Typography className={classes.bioText}>{bio ? bio : '(No Bio)'}</Typography>
        );
    };

    const renderProfileInfo = () => {
        return (
            <Box width={'100%'}>
                <Box
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                    alignItems={'flex-start'}
                >
                    <Box>
                        <Typography
                            display={'inline'}
                            variant={'h5'}
                            className={clsx(
                                classes.username,
                                preferredUsername.includes(' ') &&
                                    classes.usernameBreakWordPolicy,
                            )}
                        >
                            {displayName || preferredUsername}
                            <div className={classes.badgeGroupContainer}>
                                <div className={classes.badgeGroupInnerContainer}>
                                    {isElevatorStaff && <UserBadge variant={'staff'} />}
                                    {preferred && isOwner && (
                                        <UserBadge variant={'preferred'} />
                                    )}
                                    {verified && <UserBadge variant={'verified'} />}
                                </div>
                            </div>
                        </Typography>
                        <Box paddingBottom={4}>
                            <Typography
                                href={
                                    NAVIGATION_ROUTES.USERS.route +
                                    '/' +
                                    preferredUsername
                                }
                                component={'a'}
                                className={classes.preferredUsername}
                            >
                                @{preferredUsername}
                            </Typography>
                        </Box>
                        <Typography variant={'body2'} className={classes.role}>
                            {role}
                        </Typography>
                    </Box>

                    <Hidden smDown>
                        {isOwner && (
                            <Box>
                                {/* NOTE implement following logic */}
                                {renderEditProfileButton()}
                            </Box>
                        )}
                    </Hidden>
                </Box>
                {/*Bio and Social media on desktop */}
                <Hidden xsDown>
                    <Box paddingTop={2} maxWidth={'760px'}>
                        {renderBio()}
                    </Box>
                    {socialMedia && socialMedia.length > 0 && (
                        <Box
                            paddingTop={3}
                            display={'flex'}
                            flexDirection={'column'}
                            justifyContent={'flex-start'}
                        >
                            {renderSocialMedia()}
                        </Box>
                    )}
                </Hidden>
            </Box>
        );
    };

    return (
        <>
            {renderBannerImage()}
            <ScreenContainer disableTopPadding>
                <NextSeo
                    title={`${preferredUsername} | ELEVATOR`}
                    description={`${preferredUsername}'s ELEVATOR profile`}
                    canonical={`https://submissions.elevatormag.com/users/${preferredUsername}`}
                    openGraph={{
                        title: `${preferredUsername} | ELEVATOR`,
                        type: 'profile',
                        profile: {
                            username: preferredUsername,
                        },
                    }}
                />
                <Box width={'100%'} className={clsx(mobile && classes.offsetCard)}>
                    <Box width={'100%'} paddingX={mobile ? 2 : 0}>
                        <Paper className={classes.root}>
                            <Box
                                display={'flex'}
                                flexDirection={'row'}
                                className={classes.profileCardContainer}
                            >
                                {renderProfileImage()}
                                {renderProfileInfo()}
                            </Box>
                            {/*Bio and Social media on mobile */}
                            <Hidden smUp>
                                <Box paddingTop={2}>{renderBio()}</Box>
                                {socialMedia && socialMedia.length > 0 && (
                                    <Box
                                        paddingTop={4}
                                        display={'flex'}
                                        flexDirection={'row'}
                                        alignItems={'center'}
                                        justifyContent={'center'}
                                    >
                                        {renderSocialMedia()}
                                    </Box>
                                )}
                            </Hidden>
                            <Hidden mdUp>
                                {isOwner && (
                                    <Box paddingTop={4}>
                                        {/* NOTE implement following logic */}
                                        {renderEditProfileButton()}
                                    </Box>
                                )}
                            </Hidden>
                        </Paper>
                    </Box>
                    <RelatedPostsGrid
                        profile={profile}
                        isElevatorStaff={isElevatorStaff}
                        mobile={mobile}
                        renderLoading={renderLoading}
                    />
                </Box>
            </ScreenContainer>
        </>
    );
};

User.propTypes = {
    classes: PropTypes.object.isRequired,
    profile: PropTypes.object.isRequired,
    isOwner: PropTypes.bool.isRequired,
};

User.defaultProps = {
    isOwner: false,
};

const StyledUser = withStyles(styles)(User);
const StyledWithLoading = withLoading(StyledUser);

export default withSnackbar(StyledWithLoading);
